import { useEffect, useState } from 'react';
import { track } from '@amplitude/analytics-browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCountry } from 'commons/hooks';
import {
  configurationService,
  GetInstallmentsV2Response,
} from 'commons/services';
import {
  InstallmentsFeeResponsible,
  InstallmentV2Type,
  MoneyObjectInterface,
} from 'commons/types';
import { hasMinOrderAmountErrorMx } from './Mexico/utils';

export type SelectedInstallments = {
  withoutInterest: InstallmentV2Type[];
  planAhora?: InstallmentV2Type[];
};

type InstallmentsState = SelectedInstallments & {
  changingResponsible: boolean;
  responsible: InstallmentsFeeResponsible;
};

export type ResponsibleStatus =
  | 'customer_active'
  | 'merchant_active'
  | 'changing_to_customer'
  | 'changing_to_merchant';

export function useInstallmentsV2(opts: {
  onResponsibleChange: {
    success: (responsibleChangedTo: InstallmentsFeeResponsible) => void;
    error: () => void;
  };
}) {
  const { isArgentina, isMexico } = useCountry();

  const [installments, setInstallments] = useState<SelectedInstallments>({
    withoutInterest: [],
    planAhora: [],
  });
  const [installmentsChanged, setInstallmentsChanged] = useState(false);
  const [
    installmentMinOrderAmountChanged,
    setInstallmentMinOrderAmountChanged,
  ] = useState(false);
  const [hasSynced, setHasSynced] = useState(false);
  const changed = installmentsChanged || installmentMinOrderAmountChanged;

  const queryKey = ['installmentsV2'];
  const queryClient = useQueryClient();
  const queryInstallments = useQuery<
    GetInstallmentsV2Response,
    unknown,
    InstallmentsState
  >(queryKey, () => configurationService.getInstallmentsV2(), {
    onSuccess(data) {
      setInstallments({
        withoutInterest: data.withoutInterest,
        planAhora: data.planAhora,
      });
      setHasSynced(true);
    },
  });

  const currentResponsible = queryInstallments.data?.responsible;
  const responsibleToChange =
    currentResponsible === InstallmentsFeeResponsible.MERCHANT
      ? InstallmentsFeeResponsible.CUSTOMER
      : InstallmentsFeeResponsible.MERCHANT;
  const changingResponsible = queryInstallments.data?.changingResponsible;
  const responsibleStatus: ResponsibleStatus = changingResponsible
    ? currentResponsible === InstallmentsFeeResponsible.CUSTOMER
      ? 'changing_to_merchant'
      : 'changing_to_customer'
    : currentResponsible === InstallmentsFeeResponsible.CUSTOMER
      ? 'customer_active'
      : 'merchant_active';

  useEffect(() => {
    if (changingResponsible) {
      track('pago-nube-error-cambio-cuotas');
    }
  }, [changingResponsible]);

  const setChanged = (changed: boolean) => {
    setInstallmentsChanged(changed);
    setInstallmentMinOrderAmountChanged(changed);
  };

  const updateInstallments = (installments: SelectedInstallments) => {
    setInstallments({
      withoutInterest: installments.withoutInterest,
      planAhora: installments.planAhora,
    });
    setInstallmentsChanged(true);
  };

  const updateInstallmentMinOrderAmount = (
    id: number,
    minOrderAmount: MoneyObjectInterface,
  ) => {
    setInstallments((prev) => {
      const updatedPlanAhora = prev.planAhora?.map((installment) =>
        installment.id === id
          ? { ...installment, minOrderAmount }
          : installment,
      );

      const updatedWithoutInterest = prev.withoutInterest.map((installment) =>
        installment.id === id
          ? { ...installment, minOrderAmount }
          : installment,
      );

      return {
        ...prev,
        planAhora: updatedPlanAhora,
        withoutInterest: updatedWithoutInterest,
      };
    });

    setInstallmentMinOrderAmountChanged(true);
  };

  const mutateInstallments = useMutation(queryKey, {
    mutationFn: () => {
      // Remove minOrderAmount only for countries that don't support it
      // Argentina and Mexico need to send minOrderAmount
      const removeMinOrderAmountFrom = (installments?: InstallmentV2Type[]) =>
        installments?.map((installment) => ({
          id: installment.id,
          quantity: installment.quantity,
          enabled: installment.enabled,
          interest: installment.interest,
          type: installment.type,
        }));

      const shouldIncludeMinOrderAmount = isArgentina || isMexico;

      const installmentsToUpdate = {
        planAhora: isArgentina
          ? installments.planAhora
          : removeMinOrderAmountFrom(installments.planAhora),
        withoutInterest: shouldIncludeMinOrderAmount
          ? installments.withoutInterest
          : removeMinOrderAmountFrom(installments.withoutInterest),
      };
      return configurationService.patchInstallmentsV2(installmentsToUpdate);
    },
    onSuccess: () => {
      if (installmentMinOrderAmountChanged) {
        track('pago-nube-guardar-monto-ok');
      }
      queryInstallments.refetch();
    },
    onError: () => {
      if (installmentMinOrderAmountChanged) {
        track('pago-nube-guardar-monto-error');
      }
    },
  });

  const mutateResponsible = useMutation(
    ['responsible'],
    () => {
      return configurationService.patchInstallmentsResponsible(
        responsibleToChange,
      );
    },
    {
      onSuccess() {
        queryClient.setQueryData<InstallmentsState>(queryKey, (old) => ({
          ...old!,
          changingResponsible: true,
        }));
        opts.onResponsibleChange.success(responsibleToChange);
      },
      onError() {
        opts.onResponsibleChange.error();
      },
    },
  );

  const hasMinAmountValidationError = () => {
    if (!isMexico) return false;

    return installments.withoutInterest.some((installment) => {
      if (!installment.enabled) return false;
      if (!installment.minOrderAmount?.value) return false;

      return hasMinOrderAmountErrorMx(
        installment.minOrderAmount.value,
        installment.quantity,
      );
    });
  };

  return {
    installments: {
      withoutInterest: installments.withoutInterest,
      planAhora: installments.planAhora,
    },
    responsible: {
      status: responsibleStatus,
    },
    changed,
    isLoadingInstallments:
      queryInstallments.isLoading || (!queryInstallments.isError && !hasSynced),
    isErrorInstallments: queryInstallments.isError,
    setChanged,
    updateInstallments,
    changeResponsible: mutateResponsible,
    submitInstallments: mutateInstallments,
    updateInstallmentMinOrderAmount,
    hasMinAmountValidationError,
  };
}
